import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { DataService } from './data.service';
import {
  PlaidLinkConfig,
  PlaidLinkHandler,
  PlaidLinkMetadata,
  PlaidLinkError,
  PlaidLinkResult
} from '../models/plaid-link.model';
import { BorrowerVerificationUserEnrollmentRequest, BorrowerVerificationUserEnrollmentResponse } from '../models/borrower-verification-user-enrollment.model';

const PLAID_LINK_SCRIPT_URL = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';

@Injectable({
  providedIn: 'root'
})
export class PlaidCheckService {
  private linkHandler: PlaidLinkHandler | null = null;
  private scriptLoaded = false;
  private scriptLoading: Promise<void> | null = null;

  constructor(private readonly _dataService: DataService) {}

  initializePlaidCheckForAssets(borrowerId: number, applicationId: number): Observable<BorrowerVerificationUserEnrollmentResponse> {
    const request: BorrowerVerificationUserEnrollmentRequest = {
      applicationId: applicationId,
      borrowerId: borrowerId,
      maxHeightInPixels: 600,
      maxWidthInPixels: 800,
      integrationProvider: 'PlaidCheck',
      allowedProducts: ['Assets'],
      inviteAllApplicationBorrowers: false
    };
    return this._dataService.post('api/online-app/borrower-verification/invite', request);
  }

  initializePlaidCheckForIncome(borrowerId: number, applicationId: number): Observable<BorrowerVerificationUserEnrollmentResponse> {
    const request: BorrowerVerificationUserEnrollmentRequest = {
      applicationId: applicationId,
      borrowerId: borrowerId,
      maxHeightInPixels: 600,
      maxWidthInPixels: 800,
      integrationProvider: 'PlaidCheck',
      allowedProducts: ['Income', 'Employment'],
      inviteAllApplicationBorrowers: false
    };
    return this._dataService.post('api/online-app/borrower-verification/invite', request);
  }

  async openPlaidLink(linkToken: string): Promise<PlaidLinkResult> {
    await this.ensureScriptLoaded();

    return new Promise((resolve) => {
      const config: PlaidLinkConfig = {
        token: linkToken,
        onSuccess: (publicToken: string, metadata: PlaidLinkMetadata) => {
          console.log('Plaid Link success:', { publicToken, metadata });
          this.destroyHandler();
          resolve({ success: true, publicToken, metadata });
        },
        onExit: (error: PlaidLinkError | null, metadata: PlaidLinkMetadata) => {
          console.log('Plaid Link exit:', { error, metadata });
          this.destroyHandler();
          if (error) {
            resolve({ success: false, error, metadata });
          } else {
            resolve({ success: false, cancelled: true, metadata });
          }
        },
        onEvent: (eventName: string, metadata: PlaidLinkMetadata) => {
          this.handlePlaidEvent(eventName, metadata);
        },
        onLoad: () => {
          console.log('Plaid Link loaded');
        }
      };

      this.linkHandler = window.Plaid.create(config);
      this.linkHandler.open();
    });
  }


  getErrorMessage(error: PlaidLinkError): string {
    const errorMessages: Record<string, string> = {
      'INVALID_LINK_TOKEN': 'Session expired, please try again.',
      'INSTITUTION_NOT_FOUND': 'Bank not supported, please try a different institution.',
      'ITEM_LOGIN_REQUIRED': 'Please re-authenticate with your bank.',
      'USER_SETUP_REQUIRED': 'Additional setup required, please complete in your banking app.',
      'INTERNAL_SERVER_ERROR': 'Service temporarily unavailable, please try again later.'
    };

    return errorMessages[error.error_code] || error.display_message || error.error_message || 'An error occurred during verification.';
  }

  destroyHandler(): void {
    if (this.linkHandler) {
      this.linkHandler.destroy();
      this.linkHandler = null;
    }
  }

  private handlePlaidEvent(eventName: string, metadata: PlaidLinkMetadata): void {
    switch (eventName) {
      case 'OPEN':
        console.log('Plaid Link opened');
        break;
      case 'HANDOFF':
        console.log('User completed Plaid Link flow');
        break;
      case 'SELECT_INSTITUTION':
        console.log('User selected institution:', metadata.institution?.name);
        break;
      case 'SUBMIT_CREDENTIALS':
        console.log('User submitted credentials');
        break;
      case 'SUBMIT_MFA':
        console.log('User submitted MFA code');
        break;
      case 'ERROR':
        console.error('Plaid Link error occurred');
        break;
      case 'EXIT':
        console.log('User exited Plaid Link');
        break;
      default:
        console.log('Plaid Link event:', eventName, metadata);
    }
  }

  private async ensureScriptLoaded(): Promise<void> {
    if (this.scriptLoaded) {
      return;
    }

    if (this.scriptLoading) {
      return this.scriptLoading;
    }

    this.scriptLoading = new Promise((resolve, reject) => {
      if (window.Plaid) {
        this.scriptLoaded = true;
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = PLAID_LINK_SCRIPT_URL;
      script.async = true;
      script.onload = () => {
        this.scriptLoaded = true;
        resolve();
      };
      script.onerror = () => {
        reject(new Error('Failed to load Plaid Link script'));
      };
      document.head.appendChild(script);
    });

    return this.scriptLoading;
  }
}
